import { useEffect, useRef } from "react";
import Plotly from "plotly.js-basic-dist";
import { MonthDate } from "@/helpers/date";

interface ChartDataPoint {
  month: string;
  value: number;
  fullDate?: string; // Optional full date for hover (e.g., "2026-03")
}

interface LineChartProps {
  data: ChartDataPoint[];
  color?: string;
}

export function LineChart({ data, color = "#ff9966" }: LineChartProps) {
  const plotRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);

  // Render or update the chart when data or color changes
  useEffect(() => {
    const plotElement = plotRef.current;
    if (!plotElement) return;

    // If no data, clear any existing plot
    if (!data || data.length === 0) {
      if (isInitializedRef.current) {
        Plotly.purge(plotElement);
        isInitializedRef.current = false;
      }
      return;
    }

    // Determine the current month to split past and future
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-based (0 = January)

    // Find the split index - data points up to and including current month are "past"
    let splitIndex = data.length; // Default: all data is past (no future data)
    for (let i = 0; i < data.length; i++) {
      const point = data[i];
      if (point.fullDate) {
        const monthDate = MonthDate.parseYearMonth(point.fullDate);
        if (monthDate) {
          const pointYear = monthDate.getFullYear();
          const pointMonth = monthDate.getMonth(); // 0-based
          // If this point is after current month, this is where we split
          if (pointYear > currentYear || (pointYear === currentYear && pointMonth > currentMonth)) {
            splitIndex = i;
            break;
          }
        }
      } else {
        // Data points without fullDate are treated as past data
        // Continue to next point
      }
    }

    // Create the plot data with separate traces for past and future
    const plotData: Plotly.Data[] = [];

    // Past data (solid line) - includes up to splitIndex
    if (splitIndex > 0) {
      const pastData = data.slice(0, splitIndex);
      plotData.push({
        x: pastData.map((d) => d.month),
        y: pastData.map((d) => d.value),
        type: "scatter",
        mode: "lines",
        line: {
          color: color,
          width: 3,
          shape: "spline",
          smoothing: 0.8,
        },
        customdata: pastData.map((d) => d.fullDate || d.month),
        hovertemplate: "<b>%{customdata}</b><br>%{y:.1f}<extra></extra>",
        showlegend: false,
      });
    }

    // Future data (dashed line) - starts from splitIndex-1 to connect seamlessly with past
    // Math.max ensures we don't get negative index when splitIndex is 0 (all data is future)
    if (splitIndex < data.length) {
      const futureData = data.slice(Math.max(0, splitIndex - 1));
      plotData.push({
        x: futureData.map((d) => d.month),
        y: futureData.map((d) => d.value),
        type: "scatter",
        mode: "lines",
        line: {
          color: color,
          width: 3,
          shape: "spline",
          smoothing: 0.8,
          dash: "dash",
        },
        customdata: futureData.map((d) => d.fullDate || d.month),
        hovertemplate: "<b>%{customdata}</b><br>%{y:.1f}<extra></extra>",
        showlegend: false,
      });
    }

    const layout: Partial<Plotly.Layout> = {
      autosize: true,
      margin: { l: 10, r: 10, t: 10, b: 40 },
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      xaxis: {
        showgrid: false,
        zeroline: false,
        showline: false,
        tickfont: { color: "#9ca3af", size: 11 },
        tickangle: -45, // Rotate labels 45 degrees counterclockwise
        // allow Plotly to add padding for tick labels so first/last labels are visible
        automargin: true,
        fixedrange: true,
      },
      yaxis: {
        // Remove fixed range to allow automatic scaling based on data
        showgrid: false,
        zeroline: false,
        showline: false,
        showticklabels: false,
        fixedrange: true,
      },
      hoverlabel: {
        bgcolor: "#1f2937",
        bordercolor: color,
        font: { color: "#f3f4f6", size: 12 },
      },
    };

    const config: Partial<Plotly.Config> = {
      displayModeBar: false,
      responsive: true,
      scrollZoom: false,
      doubleClick: false,
    };

    // Use react() for efficient updates if already initialized, otherwise newPlot
    if (isInitializedRef.current) {
      Plotly.react(plotElement, plotData, layout, config);
    } else {
      Plotly.newPlot(plotElement, plotData, layout, config);
      isInitializedRef.current = true;
    }
  }, [data, color]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (plotRef.current && isInitializedRef.current) {
        Plotly.purge(plotRef.current);
        isInitializedRef.current = false;
      }
    };
  }, []);

  return <div ref={plotRef} className="w-full h-full" />;
}
